import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ClipboardCheck, Printer, User, Building2, HandCoins, Send } from "lucide-react";
import { Input } from "@/components/ui/input";

const lists = {
  individual: {
    label: "Individual Filers",
    icon: User,
    intro: "Everything a personal (Form 1040) filer should gather before tax season.",
    items: [
      "Photo ID and Social Security cards or ITINs for you, your spouse, and dependents",
      "A copy of last year's tax return",
      "W-2 forms from every employer",
      "1099-NEC / 1099-K for self-employment or gig income",
      "1099-INT and 1099-DIV for interest and dividends",
      "1099-B for brokerage or stock sales",
      "1099-R for retirement distributions; SSA-1099 for Social Security",
      "1099-G for unemployment income or state tax refunds",
      "Form 1095-A if you had Marketplace health insurance",
      "Childcare expenses and your provider's name, address, and EIN",
      "Form 1098-T (tuition) and 1098-E (student loan interest)",
      "Form 1098 mortgage interest and property tax records",
      "Charitable donation receipts (cash and non-cash)",
      "Medical and dental expense records, if itemizing",
      "IRA / HSA contribution records (Form 5498, 1099-SA)",
      "Records of estimated tax payments made during the year",
      "Bank routing and account numbers for direct-deposit refunds",
    ],
  },
  business: {
    label: "Business Filers",
    icon: Building2,
    intro: "What sole proprietors, LLCs, S-Corps, and partnerships need for a smooth filing.",
    items: [
      "EIN and legal entity documents (articles, operating agreement, S-Corp election)",
      "A copy of last year's business tax return",
      "Year-end financial statements: profit & loss and balance sheet",
      "Bank and credit card statements for every business account",
      "Payroll reports: W-2s / W-3 and quarterly 940 / 941 filings",
      "1099-NECs issued to contractors and collected W-9s",
      "Records of asset purchases and existing depreciation schedules",
      "Vehicle mileage log and auto expenses",
      "Home office square footage and related expenses",
      "Business loan statements and interest paid",
      "Beginning and ending inventory counts, if you carry inventory",
      "Estimated quarterly tax payments made during the year",
      "Sales tax returns filed during the year",
      "Owner and employee health insurance and retirement plan contributions",
    ],
  },
  budget: {
    label: "Family Budget Starter",
    icon: HandCoins,
    intro: "The first steps for families and small business owners to take control of their money.",
    items: [
      "List every source of monthly take-home income (paychecks, side income, benefits)",
      "Pull the last 3 months of bank and credit card statements",
      "Categorize spending: housing, utilities, food, transport, insurance, debt, subscriptions",
      "Cancel or pause subscriptions you haven't used in the last 60 days",
      "Write down every debt: balance, interest rate, and minimum payment",
      "Set an emergency fund target — start with $1,000, build toward 3–6 months of expenses",
      "Pick a starting budget rule (50/30/20: needs / wants / savings) and assign your numbers",
      "Open a separate savings account and set an automatic weekly transfer",
      "Set retirement contributions — at least enough to earn your full employer match",
      "Open or review an education savings plan (529) for each child",
      "Review insurance coverage: health, auto, home or renters, and life",
      "Check your credit report for free at annualcreditreport.com",
      "Set one 90-day money goal and put a monthly money check-in on your calendar",
      "Book a free BookCare consultation to turn this checklist into a personal strategy",
    ],
  },
};

function Checklist({ type }) {
  const data = lists[type];
  const [checked, setChecked] = useState({});
  const done = Object.values(checked).filter(Boolean).length;

  const toggle = (i) => setChecked((c) => ({ ...c, [i]: !c[i] }));

  const printList = () => {
    const html = `<html><head><title>BookCare LLC — Tax Prep Checklist (${data.label})</title>
      <style>body{font-family:Georgia,serif;color:#12291F;padding:40px;max-width:700px}h1{font-size:22px}li{margin:10px 0;font-size:14px}p{color:#555}</style>
      </head><body><h1>BookCare LLC — Tax Preparation Checklist<br/>${data.label}</h1>
      <p>${data.intro}</p><ul>${data.items.map((i) => `<li>☐ ${i}</li>`).join("")}</ul>
      <p>Questions? Info@bookcarellc.com · 425-345-9757 · Free consultation at bookcarellc.com</p></body></html>`;
    const frame = document.createElement("iframe");
    frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
    frame.srcdoc = html;
    frame.onload = () => {
      frame.contentWindow.focus();
      frame.contentWindow.print();
      setTimeout(() => frame.remove(), 60000);
    };
    document.body.appendChild(frame);
  };

  return (
    <div data-testid={`tax-checklist-${type}`}>
      <div className="mb-6 flex items-center justify-between gap-4">
        <p className="text-sm leading-relaxed text-pine/60">{data.intro}</p>
        <button
          onClick={printList}
          data-testid={`tax-checklist-print-${type}`}
          className="inline-flex shrink-0 items-center gap-2 border border-pine/25 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-pine/70 transition-colors duration-300 hover:border-forest hover:text-forest"
        >
          <Printer size={14} /> Print
        </button>
      </div>
      <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.25em] text-terra" data-testid={`tax-checklist-progress-${type}`}>
        {done} of {data.items.length} gathered
      </p>
      <div className="mb-6 h-1 w-full bg-linen">
        <div className="h-1 bg-forest transition-all duration-500" style={{ width: `${(done / data.items.length) * 100}%` }} />
      </div>
      <ul className="grid grid-cols-1 gap-1 md:grid-cols-2">
        {data.items.map((item, i) => (
          <li key={i}>
            <button
              onClick={() => toggle(i)}
              data-testid={`tax-checklist-item-${type}-${i}`}
              className={`flex w-full items-start gap-3 border border-transparent px-3 py-2.5 text-left text-sm leading-snug transition-colors duration-200 hover:border-linen hover:bg-white/70 ${
                checked[i] ? "text-pine/35 line-through" : "text-pine/80"
              }`}
            >
              <span
                className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center border transition-colors duration-200 ${
                  checked[i] ? "border-forest bg-forest text-bone" : "border-pine/30"
                }`}
              >
                {checked[i] && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4"><path d="M5 13l4 4L19 7" /></svg>}
              </span>
              {item}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function EmailCapture({ type }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/checklist-email`, { email, name: name || null, type });
      setSent(true);
      toast.success("Checklist sent — check your inbox!");
    } catch {
      toast.error("Could not send the email. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <p className="mt-10 border border-forest/30 bg-forest/5 px-6 py-5 text-sm text-forest" data-testid="checklist-email-success">
        The checklist is on its way to <strong>{email}</strong>. Filing questions? Book a free consultation above.
      </p>
    );
  }

  return (
    <form onSubmit={submit} className="mt-10 border-t border-linen pt-8" data-testid="checklist-email-form">
      <p className="mb-5 font-mono text-[10px] uppercase tracking-[0.25em] text-terra">
        Prefer it in your inbox? We&apos;ll email you this checklist
      </p>
      <div className="flex flex-col gap-3 sm:flex-row">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name (optional)"
          data-testid="checklist-email-name-input"
          className="rounded-none border-0 border-b border-pine/25 bg-transparent px-0 shadow-none focus-visible:ring-0 focus-visible:border-forest placeholder:text-pine/35 sm:max-w-[200px]"
        />
        <Input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          data-testid="checklist-email-input"
          className="rounded-none border-0 border-b border-pine/25 bg-transparent px-0 shadow-none focus-visible:ring-0 focus-visible:border-forest placeholder:text-pine/35"
        />
        <button
          type="submit"
          disabled={sending}
          data-testid="checklist-email-submit"
          className="inline-flex shrink-0 items-center justify-center gap-2 border border-forest bg-forest px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-transparent hover:text-forest disabled:opacity-40"
        >
          <Send size={14} /> {sending ? "Sending..." : "Email me the checklist"}
        </button>
      </div>
    </form>
  );
}

export default function TaxChecklist() {
  const [tab, setTab] = useState("individual");
  return (
    <section id="tax-checklist" data-testid="tax-checklist-section" className="border-t border-linen">
      <div className="mx-auto max-w-7xl px-6 py-28 md:px-10 md:py-36">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-6 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.35em] text-terra"
        >
          <ClipboardCheck size={14} /> Get Prepared
        </motion.p>
        <div className="mb-14 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
            className="max-w-2xl font-serif text-4xl font-medium leading-tight tracking-tight text-pine md:text-5xl"
            data-testid="tax-checklist-heading"
          >
            Tax prep &amp; budgeting <span className="italic text-forest">checklists</span>
          </motion.h2>
          <p className="max-w-sm text-sm leading-relaxed text-pine/60">
            Tick items off as you gather them, or print the list and bring everything to your
            consultation — filing goes faster when your documents are ready.
          </p>
        </div>

        <div className="mb-12 flex gap-2" data-testid="tax-checklist-tabs">
          {Object.entries(lists).map(([k, v]) => {
            const Icon = v.icon;
            return (
              <button
                key={k}
                onClick={() => setTab(k)}
                data-testid={`tax-checklist-tab-${k}`}
                className={`inline-flex items-center gap-2 border px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                  tab === k
                    ? "border-forest bg-forest text-bone"
                    : "border-linen bg-white/60 text-pine/60 hover:border-forest/40 hover:text-forest"
                }`}
              >
                <Icon size={14} /> {v.label}
              </button>
            );
          })}
        </div>

        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.76, 0, 0.24, 1] }}
          className="border border-linen bg-white/60 p-8 md:p-12"
        >
          <Checklist type={tab} />
          <EmailCapture type={tab} key={tab} />
        </motion.div>

        <p className="mt-10 max-w-2xl font-mono text-[10px] uppercase leading-relaxed tracking-[0.15em] text-pine/40" data-testid="tax-checklist-disclaimer">
          General guidance, not tax advice. Your situation may require additional documents —
          book a consultation and we&apos;ll build your exact list together.
        </p>
      </div>
    </section>
  );
}
